import type { ReactElement } from "react";
import { SLIP_LEGS as STUB_LEGS, SLIP_META as STUB_META } from "../_stubData.ts";
import type { Leg, SlipMeta } from "../_stubData.ts";
import { SrIcon, fmtMoney, fmtOdds } from "./SrIcon.tsx";
import { HINT_PRESETS, Stepper } from "./Stepper.tsx";
import type { RepairFormState } from "./UploadScreen.tsx";

interface Props {
  formState: RepairFormState;
  setFormState: (updater: (s: RepairFormState) => RepairFormState) => void;
  onRun: () => void;
  onBack: () => void;
  /** Live legs from the backend. Omit to use stub demo data. */
  legs?: ReadonlyArray<Leg>;
  /** Live session meta. Omit to use stub demo data. */
  meta?: SlipMeta;
}

export function ConfigureScreen({ formState, setFormState, onRun, onBack, legs, meta }: Props): ReactElement {
  const SLIP_LEGS: ReadonlyArray<Leg> = legs ?? STUB_LEGS;
  const SLIP_META: SlipMeta = meta ?? STUB_META;
  const setMode = (m: "bold" | "moderate" | "safer" | "conservative"): void =>
    setFormState((s) => ({ ...s, runMode: m }));
  const toggleLock = (id: string): void =>
    setFormState((s) => {
      const locks = new Set(s.locks);
      if (locks.has(id)) locks.delete(id);
      else locks.add(id);
      return { ...s, locks: Array.from(locks) };
    });

  const lockedCount = formState.locks.length;
  const hintLabels = formState.hints
    .map((id) => HINT_PRESETS.find((h) => h.id === id)?.label)
    .filter((v): v is string => Boolean(v));

  // SR-CONFIG-REAL — compute real readings from the actual extracted
  // legs. Replaces the hard-coded "92% Avg OCR" / "One leg flagged
  // below 92%" stub strings.
  const confidences = SLIP_LEGS.map((l) => l.confidence).filter((c) => Number.isFinite(c) && c > 0);
  const avgOcrPct = confidences.length > 0
    ? Math.round((confidences.reduce((a, b) => a + b, 0) / confidences.length) * 100)
    : null;
  const warnCount = SLIP_LEGS.filter((l) => l.confidence < 0.92).length;
  // Stake is not captured on the session today — surface "—" rather
  // than the stub default when we know we're rendering live data.
  const isLive = legs !== undefined;
  const stakeKnown = !isLive && SLIP_META.stake > 0;

  return (
    <div className="sr-page">
      <Stepper active="configure" />

      <div className="sr-section-head" style={{ marginTop: 0 }}>
        <h2>Confirm legs and configure repair</h2>
        <span className="meta">{SLIP_LEGS.length} {SLIP_LEGS.length === 1 ? "leg" : "legs"} extracted · adjust anything before we start</span>
        <div className="right">
          <button className="btn" onClick={onBack}>
            <SrIcon name="undo" size={12} />
            Back to upload
          </button>
        </div>
      </div>

      <div className="sr-configure">
        <div>
          <div className="sr-mode-picker">
            <div className="sr-mode-picker-head">
              <h3>Repair tier</h3>
              <span className="sub">
                Pick how much of the original payout you want to keep. Bold protects the dream; Conservative protects landing.
              </span>
            </div>
            <div className="sr-mode-options">
              {(() => {
                const orig = SLIP_META.origOdds || 0;
                const tiers: Array<{
                  key: "bold" | "moderate" | "safer" | "conservative";
                  label: string;
                  blurb: string;
                  frac: number;
                }> = [
                  { key: "bold",         label: "Bold",         blurb: "Preserves the dream — keeps most of the original payout.",         frac: 1.00 },
                  { key: "moderate",     label: "Moderate",     blurb: "Trades some upside for picks we have higher confidence in.",       frac: 0.30 },
                  { key: "safer",        label: "Safer",        blurb: "Prioritises landing — leans on higher-probability picks.",         frac: 0.10 },
                  { key: "conservative", label: "Conservative", blurb: "Smallest payout, highest landing confidence.",                     frac: 0.03 },
                ];
                return tiers.map((t) => {
                  const targetOdds = orig > 0 ? orig * t.frac : 0;
                  const targetLabel = targetOdds >= 1000
                    ? `~${Math.round(targetOdds).toLocaleString()}×`
                    : targetOdds >= 1
                      ? `~${targetOdds.toFixed(0)}×`
                      : "—";
                  return (
                    <button
                      key={t.key}
                      className={`sr-mode-opt ${t.key} ${formState.runMode === t.key ? "on" : ""}`}
                      onClick={() => setMode(t.key)}
                    >
                      <div className="ttl">
                        <span className="pip" />
                        {t.label}
                      </div>
                      <div className="sub">{t.blurb}</div>
                      <div className="thresh">
                        Target combined <span className="v">{targetLabel}</span>
                      </div>
                    </button>
                  );
                });
              })()}
            </div>
          </div>

          <div className="sr-confirm">
            <div className="sr-confirm-head">
              <h2>Extracted legs</h2>
              <span className="sub">
                {SLIP_META.bookmaker} · slip #{SLIP_META.slipCode} · {lockedCount} locked · click 🔒 to keep a leg untouched
              </span>
            </div>

            <div className="sr-confirm-row config head">
              <span>#</span>
              <span>Fixture</span>
              <span>Market</span>
              <span>Selection</span>
              <span style={{ textAlign: "right" }}>Odds</span>
              <span>OCR confidence</span>
              <span style={{ textAlign: "center" }}>Lock</span>
            </div>

            {SLIP_LEGS.map((leg) => {
              const warn = leg.confidence < 0.92;
              const locked = formState.locks.includes(leg.id);
              return (
                <div className="sr-confirm-row config" key={leg.id}>
                  <span className="ord">L{String(leg.n).padStart(2, "0")}</span>
                  <span
                    className="fx"
                    style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {leg.fixture.home} vs {leg.fixture.away}
                  </span>
                  <span className="mkt">{leg.original.market}</span>
                  <span className="sel">{leg.original.selection}</span>
                  <span className="odds">{fmtOdds(leg.original.odds)}</span>
                  <span className={`conf ${warn ? "warn" : ""}`}>
                    <span className="pct">{Math.round(leg.confidence * 100)}%</span>
                    <span className="bar">
                      <span className="fill" style={{ width: `${leg.confidence * 100}%` }} />
                    </span>
                  </span>
                  <span style={{ display: "flex", justifyContent: "center" }}>
                    <button
                      className={`sr-lock-btn ${locked ? "on" : ""}`}
                      onClick={() => toggleLock(leg.id)}
                      title={locked ? "Locked — repairer will leave this leg alone" : "Click to lock this leg"}
                    >
                      <SrIcon name="lock" size={12} />
                    </button>
                  </span>
                </div>
              );
            })}

            <div className="sr-confirm-foot">
              <SrIcon name="info" size={13} />
              <div className="note">
                {warnCount === 0
                  ? `All ${SLIP_LEGS.length} legs read cleanly. Edit inline if any selection looks wrong.`
                  : `${warnCount} leg${warnCount === 1 ? "" : "s"} flagged below 92% confidence — review them inline before running the repair.`}
              </div>
            </div>
          </div>

          <div className="sr-run-row">
            <div className="note">
              <strong style={{ color: "var(--text)", fontWeight: 600 }}>Step 2 of 4</strong> — review legs, set
              guardrails, then run the repair. You'll see every decision with full reasoning before anything is
              saved.
            </div>
            <div className="actions">
              <button className="btn" onClick={onBack}>
                <SrIcon name="undo" size={12} />
                Back
              </button>
              <button className="btn primary" onClick={onRun}>
                <SrIcon name="wrench" size={12} />
                Run repair ·{" "}
                {formState.runMode.charAt(0).toUpperCase() + formState.runMode.slice(1)}
                <SrIcon name="arrow" size={12} />
              </button>
            </div>
          </div>
        </div>

        <div className="sr-config-aside">
          <div className="sr-aside-card">
            <div className="sr-aside-card-head">
              <div className="ic">
                <SrIcon name="info" size={13} />
              </div>
              <h4>Your instructions</h4>
            </div>
            <div className="sr-aside-card-body">
              {formState.instructions ? (
                <div className="instr-echo">"{formState.instructions}"</div>
              ) : (
                <div className="instr-echo empty">
                  No free-text instructions provided. The repairer will use its defaults.
                </div>
              )}
              {hintLabels.length > 0 && (
                <div className="hint-list">
                  {hintLabels.map((l, i) => (
                    <div className="hint" key={i}>
                      <span className="ic">
                        <SrIcon name="check" size={9} />
                      </span>
                      {l}
                    </div>
                  ))}
                </div>
              )}
              {hintLabels.length === 0 && (
                <div
                  style={{
                    marginTop: 10,
                    fontSize: 11.5,
                    color: "var(--text-tertiary)",
                    lineHeight: 1.5,
                  }}
                >
                  No house rules set.{" "}
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onBack();
                    }}
                    style={{ color: "var(--accent-text)" }}
                  >
                    Go back
                  </a>{" "}
                  to add some.
                </div>
              )}
            </div>
          </div>

          <div className="sr-aside-card">
            <div className="sr-aside-card-head">
              <div className="ic">
                <SrIcon name="file" size={13} />
              </div>
              <h4>Slip summary</h4>
            </div>
            <div className="sr-aside-card-body">
              <div className="stat-grid">
                <div className="cell">
                  <div className="k">Bookmaker</div>
                  <div className="v" style={{ fontSize: 13 }}>
                    {SLIP_META.bookmaker}
                  </div>
                </div>
                <div className="cell">
                  <div className="k">Legs</div>
                  <div className="v">{SLIP_META.legCount}</div>
                </div>
                <div className="cell">
                  <div className="k">Stake</div>
                  <div className="v" style={{ fontSize: 13 }}>
                    {stakeKnown ? `${SLIP_META.currency}${fmtMoney(SLIP_META.stake)}` : "—"}
                  </div>
                </div>
                <div className="cell">
                  <div className="k">Original odds</div>
                  <div className="v">{fmtOdds(SLIP_META.origOdds)}×</div>
                </div>
                <div className="cell">
                  <div className="k">Locked legs</div>
                  <div className="v">{lockedCount}</div>
                </div>
                <div className="cell">
                  <div className="k">Avg OCR</div>
                  <div className="v">{avgOcrPct != null ? `${avgOcrPct}%` : "—"}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="sr-aside-card">
            <div className="sr-aside-card-head">
              <div className="ic">
                <SrIcon name="shield" size={13} />
              </div>
              <h4>The first rule</h4>
            </div>
            <div
              className="sr-aside-card-body"
              style={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.55 }}
            >
              <strong style={{ color: "var(--text)", fontWeight: 600 }}>Do no unnecessary harm.</strong> Legs above
              threshold stay exactly as you placed them. Locked legs are never touched. You accept, override, or
              remove each replacement after seeing the reasoning.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
